import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, createSearchParams, useLocation } from 'react-router-dom';
import classnames from 'classnames';
import _ from 'lodash';
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Label,
  FormGroup,
  Form,
  Input,
  Table,
  Row,
  Col,
  Spinner,
  UncontrolledTooltip,
  TabPane,
  CardFooter,
} from 'reactstrap';
import PageTabs from 'components/PageTabs';
import { FixedCenterLoader } from 'components/Loaders';
import TimePeriod, { formatDates } from 'components/TimePeriod';
import { AutoColumns } from 'components/Columns';
import { useArrayState } from 'components/CustomHooks';
import DownloadSimpleTable from 'components/DownloadSimpleTable';
import ReactDatetime from 'react-datetime';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { getPerformanceEngineBenchmark } from 'services/analysis';
import { optionizeValue, optionizeAll, getValue, getDate, getYearMonth, monthDiff } from 'helpers/select';
import { confidenceLevelToInterval } from 'helpers/formatter';
import { toDeci, toPerc, toPercTable, toDeciTable, tablePresets, getDataType, formatCurrency } from 'helpers/formatter';
import PieChart from 'components/PieChart';
import TightTable from 'components/TightTable';
import { Descriptions, Comments, Notes } from '../others';
import AssetSelect from '../AssetSelect';
import Summary from './benchmark-analysis/Summary';
import RiskMetrics from './benchmark-analysis/RiskMetrics';
import AdvancedDetail from './benchmark-analysis/AdvancedDetail';
import Charts from './benchmark-analysis/Charts';
import MultipleAssetSelect from '../MultipleAssetSelect';
import SingleAssetHeader from 'views/SingleAssetHeader';
import ToggleContainer from 'components/ToggleContainer';

function BenchmarkAnalysis({ session }) {
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState({
    product: '',
    database: '',
    benchmark: '',
    converter: '',
  });
  const [result, setResult] = useState(null);

  const fetchData = async () => {
    setLoading(true);

    const [data, err] = await getPerformanceEngineBenchmark(options);

    setResult(data);
    setLoading(false);
  };

  useEffect(() => {
    if (!options.product) return;
    fetchData();
  }, [options]);

  let main = null;
  if (loading) {
    main = <FixedCenterLoader />;
  } else if (result) {
    main = (
      <>
        <SingleAssetHeader data={result.header} />

        <Descriptions content={result.description} />

        {/* {result.outperformance && (
          <Comments contents={[result.outperformance['line 1'], result.outperformance['line 2']]} />
        )} */}

        <PageTabs
          options={[
            { value: 'summary', label: 'Summary' },
            { value: 'risk-metrics', label: 'Risk Metrics' },
            { value: 'advanced-detail', label: 'Advanced Detail' },
            { value: 'charts', label: 'Charts' },
          ]}
          colorIndex={0}
        >
          <TabPane tabId="summary" role="tabpanel">
            <Summary data={result.summary} params={options} />
          </TabPane>
          <TabPane tabId="risk-metrics" role="tabpanel">
            <RiskMetrics data={result.risk_metrics} params={options} />
          </TabPane>
          <TabPane tabId="advanced-detail" role="tabpanel">
            <AdvancedDetail data={result.advanced_detail} params={options} />
          </TabPane>
          <TabPane tabId="charts" role="tabpanel">
            <Charts data={{ ...result.landing_chart, valid_windows: result.valid_windows }} params={options} />
          </TabPane>
        </PageTabs>
        <Notes content={result.notes} />
      </>
    );
  }

  const sideMenu = (
    <AssetSelect
      session={session}
      onSelect={({ product, database, benchmark, converter }) => {
        setOptions({ product, database, benchmark, converter });
      }}
      onAllowed={({ product, database, benchmark, converter }) => !!(database && product && benchmark && converter)}
      enableBenchmark
      benchmarkRequired
      databaseFilter={(db) =>
        !['Asset Classes', 'Asset Allocations', 'Listed Equities (US)', 'Listed Equities (Canada)'].includes(db)
      }
    />
  );

  const mainContent = <div key={options.product}>{main}</div>;

  return (
    <ToggleContainer
      sideMenu={sideMenu}
      mainContent={options.product && mainContent}
      classNames={{ sideMenu: 'tw-w-[400px]' }}
    />
  );
}

export default BenchmarkAnalysis;
